import React from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { getAvatarIcon } from '../../constants/avatarIcons';

interface UserAvatarProps {
  firstName?: string | null;
  email?: string | null;
  avatarIcon?: string | null;
  size?: number;
  /**
   * Sobrescribe el relleno del disco. Lo usa el header de modo piso, donde el
   * teal de acción no se despega del teal tinta del chrome. Va por prop y no
   * por CSS porque el relleno se aplica en estilo inline: pisarlo desde una
   * hoja exigiría !important sobre la clase interna .ant-avatar de AntD.
   */
  background?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Avatar de una persona, con la cadena de respaldo en un solo lugar:
 * icono elegido → inicial del nombre → inicial del correo → icono genérico.
 *
 * Vive como componente compartido y no como helper suelto porque la cadena se
 * usa en tres superficies (chip del header, modal de perfil y la lista de
 * Usuarios) y basta con que una se salte un eslabón para que la misma persona
 * se vea distinta en dos pantallas.
 *
 * El icono genérico es el último recurso real: el layout se monta antes de que
 * el perfil termine de cargar, y en la lista de Usuarios puede haber filas sin
 * nombre todavía.
 */
/**
 * Inicial que representa a una persona: nombre, si no correo, si no nada.
 *
 * Exportada porque la cadena tiene que existir UNA vez. El selector de icono y
 * el modal de perfil muestran la misma letra que el avatar; si cada uno la
 * calcula por su cuenta, basta que una copia se salte el respaldo al correo
 * para que la misma persona se vea distinta en dos pantallas.
 */
export const getUserInitial = (
  firstName?: string | null,
  email?: string | null
): string =>
  (firstName?.trim()?.charAt(0) || email?.trim()?.charAt(0) || '').toUpperCase();

const UserAvatar: React.FC<UserAvatarProps> = ({
  firstName,
  email,
  avatarIcon,
  size = 32,
  background = 'var(--color-primary)',
  className,
  style,
}) => {
  const icon = getAvatarIcon(avatarIcon);
  const initial = getUserInitial(firstName, email);

  return (
    <Avatar
      size={size}
      className={className}
      icon={icon ?? (initial ? undefined : <UserOutlined />)}
      style={{
        backgroundColor: background,
        flexShrink: 0,
        fontWeight: 700,
        // fontSize SOLO cuando se dibuja la inicial. El style del caller se
        // esparce al final en AntD, así que ponerlo siempre le ganaba al
        // tamaño que Avatar calcula para un icono (la mitad del disco) y los
        // iconos salían al 44% — 12px en la lista de Usuarios.
        ...(icon ? {} : { fontSize: Math.round(size * 0.44) }),
        ...style,
      }}
    >
      {icon ? undefined : initial || undefined}
    </Avatar>
  );
};

export default UserAvatar;
